import React, { useEffect, useState } from "react";
import { api, brl } from "@/api";
import { Money, HideMoneyButton } from "@/contexts/HiddenValues";
import { Card } from "@/components/ui/card";
import { ArrowUpRight, Package, ShoppingCart, ClipboardList, Wallet, Plus, Calendar, Sun, Wrench, Calculator } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";

function localDayStartISO(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
}
function localDayEndISO(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
}

export default function Dashboard() {
  const { canSeeFinance } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ sold: 0, profit: 0, pending: 0, products: 0, openServices: 0 });
  const [periods, setPeriods] = useState({
    month: { sold: 0, profit: 0 },
    today: { sold: 0, profit: 0 },
  });

  useEffect(() => {
    const fetchStats = async () => {
      const calls = [
        api.get("/orders", { params: { history: false }, _cache: false }),
        api.get("/products", { params: { light: true }, _cache: false }),
        api.get("/service/orders", { _cache: false }),
      ];
      if (canSeeFinance) calls.unshift(api.get("/financial/summary", { _cache: false }));
      const results = await Promise.all(calls);
      let i = 0;
      const fin = canSeeFinance ? results[i++].data : { total_sold: 0, net_profit: 0 };
      const pending = results[i++].data;
      const prods = results[i++].data;
      const services = results[i++].data;
      const openServices = services.filter(o =>
        !["Concluído / Entregue", "Sem conserto", "Recusado pelo cliente"].includes(o.status)
      ).length;
      setStats({
        sold: fin.total_sold,
        profit: fin.net_profit,
        pending: pending.length,
        products: prods.length,
        openServices,
      });
    };
    fetchStats();
    const onVisible = () => { if (document.visibilityState === "visible") fetchStats(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", fetchStats);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", fetchStats);
    };
  }, [canSeeFinance]);

  // Carrega resumos mensal e do dia (ambos só se canSeeFinance)
  useEffect(() => {
    if (!canSeeFinance) return;
    const fetchPeriods = async () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const monthStart = localDayStartISO(startOfMonth);
      const monthEnd = localDayEndISO(today);
      const todayStart = localDayStartISO(today);
      const todayEnd = localDayEndISO(today);
      const [m, t] = await Promise.all([
        api.get("/financial/summary", { params: { start: monthStart, end: monthEnd }, _cache: false }),
        api.get("/financial/summary", { params: { start: todayStart, end: todayEnd }, _cache: false }),
      ]);
      setPeriods({
        month: { sold: m.data.total_sold, profit: m.data.net_profit },
        today: { sold: t.data.total_sold, profit: t.data.net_profit },
      });
    };
    fetchPeriods();
    // Re-fetch quando a janela voltar a ficar visível (usuário voltou da aba
    // de Vendas/Pedidos depois de finalizar/marcar como Pago).
    const onVisible = () => { if (document.visibilityState === "visible") fetchPeriods(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", fetchPeriods);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", fetchPeriods);
    };
  }, [canSeeFinance]);

  const cards = [
    canSeeFinance && { label: "Total vendido", value: <Money value={stats.sold}/>, icon: ShoppingCart, link: "/historico" },
    canSeeFinance && { label: "Lucro líquido", value: <Money value={stats.profit}/>, icon: Wallet, link: "/financeiro" },
    { label: "Pedidos pendentes", value: stats.pending, icon: ClipboardList, link: "/pedidos" },
    { label: "Produtos cadastrados", value: stats.products, icon: Package, link: "/produtos" },
  ].filter(Boolean);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="label-tiny mb-2">Visão geral</p>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Painel</h1>
          <p className="text-neutral-500 mt-2">Resumo da sua operação em tempo real.</p>
        </div>
        {canSeeFinance && <HideMoneyButton testId="dashboard-hide-money"/>}
      </div>

      {/* Atalhos: Vender + Pedidos + Assistência */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="dashboard-shortcuts">
        <Link to="/vendas" className="block group" data-testid="quick-sell-button">
          <Card className="p-6 border-[#E5E5E5] shadow-none bg-[#0A0A0A] text-white hover:-translate-y-1 transition-transform h-full">
            <div className="flex items-start justify-between gap-3">
              <div className="w-12 h-12 rounded-full bg-white text-[#0A0A0A] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Plus size={22}/>
              </div>
              <ArrowUpRight size={18} className="text-white/60 group-hover:text-white transition"/>
            </div>
            <p className="label-tiny text-white/50 mt-6">Atalho</p>
            <h2 className="font-display text-2xl tracking-tight mt-1">Vender</h2>
            <p className="text-white/70 text-xs mt-2">Registre uma venda agora — produtos, cliente, recibo.</p>
            {/* Sub-atalho discreto: abrir Simulador de Taxas sem sair do card "Vender" */}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/simulador-taxas"); }}
              className="mt-4 flex items-center justify-between w-full px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 transition text-xs text-white/90"
              data-testid="quick-fee-simulator-button"
            >
              <span className="flex items-center gap-2"><Calculator size={14}/>Simulador de Taxas</span>
              <ArrowUpRight size={12} className="opacity-60 group-hover:opacity-100"/>
            </button>
          </Card>
        </Link>

        <Link to="/pedidos" className="block group" data-testid="quick-orders-button">
          <Card className="p-6 border-[#E5E5E5] shadow-none hover:-translate-y-1 transition-transform h-full hover:shadow-md">
            <div className="flex items-start justify-between gap-3">
              <div className="w-12 h-12 rounded-full bg-neutral-100 text-[#0A0A0A] flex items-center justify-center group-hover:scale-110 transition-transform">
                <ClipboardList size={22}/>
              </div>
              {stats.pending > 0
                ? <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full" data-testid="pending-orders-badge">{stats.pending} pendente{stats.pending > 1 ? "s" : ""}</span>
                : <ArrowUpRight size={18} className="text-neutral-400 group-hover:text-[#0A0A0A] transition"/>}
            </div>
            <p className="label-tiny mt-6">Acompanhar</p>
            <h2 className="font-display text-2xl tracking-tight mt-1">Pedidos</h2>
            <p className="text-neutral-500 text-xs mt-2">Pedidos do catálogo e vendas em aberto.</p>
          </Card>
        </Link>

        <Link to="/assistencia" className="block group" data-testid="quick-service-button">
          <Card className="p-6 border-[#E5E5E5] shadow-none hover:-translate-y-1 transition-transform h-full hover:shadow-md">
            <div className="flex items-start justify-between gap-3">
              <div className="w-12 h-12 rounded-full bg-neutral-100 text-[#0A0A0A] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Wrench size={22}/>
              </div>
              {stats.openServices > 0
                ? <span className="text-[11px] font-bold bg-blue-100 text-blue-800 px-2.5 py-1 rounded-full" data-testid="open-services-badge">{stats.openServices} aberta{stats.openServices > 1 ? "s" : ""}</span>
                : <ArrowUpRight size={18} className="text-neutral-400 group-hover:text-[#0A0A0A] transition"/>}
            </div>
            <p className="label-tiny mt-6">Operação</p>
            <h2 className="font-display text-2xl tracking-tight mt-1">Assistência</h2>
            <p className="text-neutral-500 text-xs mt-2">Ordens de serviço, status e termos.</p>
          </Card>
        </Link>
      </div>

      {/* Resumo do mês + de hoje */}
      {canSeeFinance && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="dashboard-period-summary">
          <Card className="p-6 border-[#E5E5E5] shadow-none" data-testid="dashboard-month-card">
            <div className="flex items-center gap-2 mb-4">
              <Calendar size={16} className="text-neutral-500"/>
              <p className="label-tiny">Histórico mensal</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-neutral-500">Faturamento</div>
                <div className="font-display text-2xl mt-1" data-testid="month-revenue"><Money value={periods.month.sold}/></div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Lucro</div>
                <div className="font-display text-2xl mt-1 text-emerald-700" data-testid="month-profit"><Money value={periods.month.profit}/></div>
              </div>
            </div>
            <Link to="/financeiro" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#0A0A0A] mt-4">
              Ver detalhes <ArrowUpRight size={12}/>
            </Link>
          </Card>

          <Card className="p-6 border-[#E5E5E5] dark:border-white/20 dark:border-2 shadow-none bg-[#0A0A0A] dark:bg-gradient-to-br dark:from-indigo-900 dark:to-purple-900 text-white" data-testid="dashboard-today-card">
            <div className="flex items-center gap-2 mb-4">
              <Sun size={16} className="text-white/70 dark:text-amber-300"/>
              <p className="label-tiny text-white/70 dark:text-white">Histórico de hoje</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-white/70 dark:text-white/80">Faturamento</div>
                <div className="font-display text-2xl mt-1 text-white" data-testid="today-revenue"><Money value={periods.today.sold}/></div>
              </div>
              <div>
                <div className="text-xs text-white/70 dark:text-white/80">Lucro</div>
                <div className="font-display text-2xl mt-1 text-emerald-300 dark:text-emerald-200" data-testid="today-profit"><Money value={periods.today.profit}/></div>
              </div>
            </div>
            <p className="text-[11px] text-white/60 dark:text-white/70 mt-4">Atualiza automaticamente todo dia.</p>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c) => (
          <Link to={c.link} key={c.label} className="group">
            <Card className="p-6 border-[#E5E5E5] hover:-translate-y-1 transition-transform shadow-none hover:shadow-md">
              <div className="flex items-start justify-between">
                <c.icon size={20} className="text-neutral-400" />
                <ArrowUpRight size={16} className="text-neutral-400 group-hover:text-[#0A0A0A] transition" />
              </div>
              <div className="mt-8">
                <div className="label-tiny">{c.label}</div>
                <div className="font-display text-3xl tracking-tight mt-2">{c.value}</div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-8 border-[#E5E5E5] shadow-none bg-neutral-50">
          <p className="label-tiny">Catálogo público</p>
          <h3 className="font-display text-2xl mt-2">Compartilhe sua loja</h3>
          <p className="text-neutral-600 text-sm mt-2">Envie o link do seu catálogo para clientes finalizarem pedidos sozinhos.</p>
          <Link to="/catalogo-admin" className="inline-flex items-center gap-2 mt-6 text-sm underline">
            Configurar catálogo <ArrowUpRight size={14} />
          </Link>
        </Card>
        <Card className="p-8 border-[#E5E5E5] shadow-none">
          <p className="label-tiny">Próximos passos</p>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex justify-between"><span>Cadastrar dados da loja</span><Link to="/loja" className="underline">Ir</Link></li>
            <li className="flex justify-between"><span>Adicionar produtos</span><Link to="/produtos" className="underline">Ir</Link></li>
            <li className="flex justify-between"><span>Abrir uma OS de assistência</span><Link to="/assistencia" className="underline">Ir</Link></li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
